import React, { useEffect } from 'react';
import { forTemplate, localizeCount } from 'app/common/lib/util/i18n';

import styles from './NonEnterpriseTeamsPage.less';
import { Icon, IconName } from 'app/common/components/controls';
import {
  useClaimableOrganizationsQuery,
  useClaimableOrganizationAdminsQuery,
  useClaimableOrganizationTransferrableDataQuery,
  useClaimOrganizationMutation,
} from '@trello/graphql/generated';
import { OrganizationFeatures } from '@trello/product-features';
import { Spinner } from 'app/src/components/Spinner';
import { usePopover, Popover } from '@trello/nachos';
import { MemberAvatar } from 'app/src/components/MemberAvatar';
import {
  Button,
  ButtonTypes,
} from 'app/common/components/controls/button/button';
import { ButtonLink } from 'app/common/components/controls/link/link';
import { AlertContainer, useAlert } from 'app/gamma/src/components/alert';
import { localizeServerError } from '@trello/app/common/lib/util/i18n';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { Analytics } from '@trello/atlassian-analytics/src/AnalyticsClient';

const format = forTemplate('enterprise_non_enterprise_teams');

const MAX_NUM_TEAMS = 500;
const TRANSFER_ERROR_ALERT_NAME = 'TRANSFER_ERROR_ALERT';
const TRANSFER_ERROR_AREA_NAME = 'TRANSFER_ERROR_AREA';

const PageInfoSection = ({
  nonEnterpriseTeamCount,
}: {
  nonEnterpriseTeamCount: number;
}) => (
  <div className={styles.pageInfoSection}>
    <span className={styles.eligibleForUpgradeHeading}>
      {format('non-enterprise-teams-heading', {
        nonEnterpriseTeamCount: nonEnterpriseTeamCount,
      })}
    </span>
    <span>{format('ordered-by-active-member-count')}</span>
    <p className={styles.eligibleForUpgradeSubtext}>
      {format('non-enterprise-teams-subtext')}
    </p>
  </div>
);

interface ClaimableTeam {
  id: string;
  products: number[];
  name: string;
  displayName: string;
  idActiveAdmins: string[];
  activeMembershipCount: number;
}

interface EnterpriseProps {
  idEnterprise: string;
}

const TeamNameAndType = ({
  displayName,
  products,
}: Pick<ClaimableTeam, 'displayName' | 'products'>) => (
  <div className={styles.teamNameAndTypeSection}>
    <Icon name={IconName.Organization} className={styles.teamIcon} large />
    <div className={styles.teamNameAndType}>
      <span className={styles.teamName}>{displayName}</span>
      <span className={styles.teamType}>
        {OrganizationFeatures.isBusinessClassProduct(products[0])
          ? format('bc-team')
          : format('free-team')}
      </span>
    </div>
  </div>
);

const AdminsPopoverContent = ({
  idActiveAdmins,
}: Pick<ClaimableTeam, 'idActiveAdmins'>) => {
  const { data } = useClaimableOrganizationAdminsQuery({
    variables: {
      ids: idActiveAdmins,
    },
  });

  if (!data)
    return (
      <div>
        <Spinner centered />
      </div>
    );

  return (
    <>
      {data.members.map((admin) => {
        const username = admin.username;
        const fullName = admin.nonPublic?.fullName || admin.fullName;

        return (
          <div className={styles.adminListItem} key={admin.id}>
            <MemberAvatar
              size={32}
              idMember={admin.id}
              className={styles.memberAvatar}
            />
            {fullName && (
              <>
                <span className={styles.memberFullName}>{fullName}</span>
                <span className={styles.memberUsernameWrapper}>
                  <span>(</span>
                  <span className={styles.memberUsername}>{username}</span>
                  <span>)</span>
                </span>
              </>
            )}
            {!fullName && (
              <span className={styles.memberUsernameWrapper}>
                <span className={styles.memberUsername}>{username}</span>
              </span>
            )}
          </div>
        );
      })}
    </>
  );
};

const AdminCountButton = ({
  idActiveAdmins,
  idOrganization,
  idEnterprise,
}: Pick<ClaimableTeam, 'idActiveAdmins'> & {
  idEnterprise: string;
  idOrganization: string;
}) => {
  const { triggerRef, popoverProps, toggle } = usePopover<HTMLButtonElement>();

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => {
          trackUe({
            category: Noun.ENT_DASH_NON_ENT_TEAMS_TAB,
            verb: Verb.SHOWS,
            directObj: Noun.ADMIN_LIST_POPOVER,
            context: {
              enterpriseId: idEnterprise,
              organizationId: idOrganization,
            },
          });
          Analytics.sendScreenEvent({
            name: 'adminListInlineDialog',
          });

          toggle();
        }}
        className={styles.adminButton}
      >
        {localizeCount('admins', idActiveAdmins.length)}
      </button>
      <Popover title={format('admins')} {...popoverProps}>
        <AdminsPopoverContent idActiveAdmins={idActiveAdmins} />
      </Popover>
    </>
  );
};

const TeamMemberCounts = ({
  activeMembershipCount,
  idActiveAdmins,
  idOrganization,
  idEnterprise,
}: Pick<ClaimableTeam, 'activeMembershipCount' | 'idActiveAdmins'> & {
  idEnterprise: string;
  idOrganization: string;
}) => {
  return (
    <div className={styles.teamMemberCountsSection}>
      {localizeCount('members-capitalized', activeMembershipCount)}
      <span className={styles.memberCountBreakdown}>
        <AdminCountButton
          idActiveAdmins={idActiveAdmins}
          idOrganization={idOrganization}
          idEnterprise={idEnterprise}
        />
        {activeMembershipCount > 0 && (
          <>
            , {format('active-count', { activeCount: activeMembershipCount })}
          </>
        )}
      </span>
    </div>
  );
};

const AddToEnterprisePopoverContent = ({
  idOrganization,
  idEnterprise,
}: {
  idOrganization: ClaimableTeam['id'];
} & EnterpriseProps) => {
  const {
    data: claimableOrgsData,
    refetch: refetchClaimableOrgs,
    loading: isLoadingClaimableOrgs,
  } = useClaimableOrganizationsQuery({
    variables: {
      id: idEnterprise,
    },
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: transferrableData,
    loading: isLoadingTransferrableData,
    error: transferrableDataError,
  } = useClaimableOrganizationTransferrableDataQuery({
    variables: {
      idOrganization,
      idEnterprise,
    },
  });

  const [
    claimOrganization,
    { loading: isClaimingOrganization, error: claimOrganizationError },
  ] = useClaimOrganizationMutation({
    variables: {
      idOrganization,
      idEnterprise,
    },
  });

  const { showAlert } = useAlert({
    alertArea: TRANSFER_ERROR_AREA_NAME,
    alertName: TRANSFER_ERROR_ALERT_NAME,
  });

  useEffect(() => {
    if (transferrableDataError) {
      showAlert();
      refetchClaimableOrgs();
    }
  }, [refetchClaimableOrgs, showAlert, transferrableDataError]);

  if (
    isLoadingClaimableOrgs ||
    isLoadingTransferrableData ||
    isClaimingOrganization
  ) {
    return (
      <div>
        <Spinner centered />
        {transferrableDataError && (
          <AlertContainer
            displayType="error"
            area={TRANSFER_ERROR_AREA_NAME}
            name={TRANSFER_ERROR_ALERT_NAME}
          >
            {localizeServerError(
              transferrableDataError.networkError?.message ||
                transferrableDataError.message,
            )}
          </AlertContainer>
        )}
      </div>
    );
  }

  if (!claimableOrgsData?.enterprise?.licenses) {
    throw new Error(
      `Unreachable error hit: organization ${idOrganization}, enterprise ${idEnterprise}`,
    );
  }

  const {
    enterprise: {
      licenses: { maxMembers, totalMembers },
    },
  } = claimableOrgsData;

  const { newBillableMembers = [], restrictedMembers = [] } =
    transferrableData?.enterprise?.transferrableData || {};

  const additionalSeatsRequired =
    typeof maxMembers === 'number'
      ? newBillableMembers.length - (maxMembers - totalMembers)
      : 0;

  if (additionalSeatsRequired > 0) {
    return (
      <div className={styles.addToEnterprisePopoverContent}>
        <p>
          {format('need-additional-seats-to-upgrade', {
            enterpriseSeats: localizeCount(
              'Enterprise seats',
              additionalSeatsRequired,
            ),
          })}
        </p>
        <ButtonLink
          className={styles.contactSalesButton}
          link="/enterprise#contact"
          isPrimary
          openInNewTab
        >
          {format('contact-sales')}
        </ButtonLink>
      </div>
    );
  }

  const additionalSeatsString = localizeCount(
    'additional seats',
    newBillableMembers.length,
  );
  const restrictedMembersString = localizeCount(
    'restricted members',
    restrictedMembers.length,
  );

  const addToEnterprise = async () => {
    try {
      await claimOrganization();
      refetchClaimableOrgs();
    } catch (err) {
      showAlert();
    }
  };

  const message =
    restrictedMembers.length > 0
      ? format('use-additional-seats-remove-restricted-members', {
          additionalSeats: additionalSeatsString,
          restrictedMembers: restrictedMembersString,
        })
      : format('use-additional-seats', {
          additionalSeats: additionalSeatsString,
        });

  return (
    <div className={styles.addToEnterprisePopoverContent}>
      <p>{message}</p>
      <Button
        buttonType={ButtonTypes.PRIMARY}
        className={styles.addButton}
        onClick={() => {
          trackUe({
            category: Noun.ENT_DASH_NON_ENT_TEAMS_TAB,
            verb: Verb.ADDS,
            directObj: Noun.ORGANIZATION,
            indirectObj: Noun.ENTERPRISE,
            method: 'by clicking button in "Add to Enterprise" popover',
            context: {
              enterpriseId: idEnterprise,
              organizationId: idOrganization,
            },
          });
          Analytics.sendUIEvent({
            action: 'clicked',
            actionSubject: 'button',
            actionSubjectId: 'addToEnterpriseButton',
            source: 'addToEnterpriseInlineDialog',
            containers: {
              enterprise: {
                id: idEnterprise,
              },
              organization: {
                id: idOrganization,
              },
            },
          });
          addToEnterprise();
        }}
      >
        {format('add')}
      </Button>
      {claimOrganizationError && (
        <AlertContainer
          displayType="error"
          area={TRANSFER_ERROR_AREA_NAME}
          name={TRANSFER_ERROR_ALERT_NAME}
        >
          {localizeServerError(
            claimOrganizationError.networkError?.message ||
              claimOrganizationError.message,
          )}
        </AlertContainer>
      )}
    </div>
  );
};

const AddToEnterpriseButton = ({
  idOrganization,
  idEnterprise,
}: { idOrganization: ClaimableTeam['id'] } & EnterpriseProps) => {
  const { triggerRef, popoverProps, toggle } = usePopover<HTMLButtonElement>();

  return (
    <div>
      <Button
        ref={triggerRef}
        onClick={() => {
          trackUe({
            category: Noun.ENT_DASH_NON_ENT_TEAMS_TAB,
            verb: Verb.SHOWS,
            directObj: Noun.ADD_TO_ENTERPRISE_POPOVER,
            context: {
              enterpriseId: idEnterprise,
              organizationId: idOrganization,
            },
          });
          Analytics.sendScreenEvent({
            name: 'addToEnterpriseInlineDialog',
          });
          toggle();
        }}
        className={styles.addToEnterpriseButton}
      >
        {format('add-to-enterprise')}
      </Button>
      <Popover title={format('add-to-enterprise')} {...popoverProps}>
        <AddToEnterprisePopoverContent
          idOrganization={idOrganization}
          idEnterprise={idEnterprise}
        />
      </Popover>
    </div>
  );
};

const Team = ({
  team,
  idEnterprise,
}: { team: ClaimableTeam } & EnterpriseProps) => (
  <div className={styles.team}>
    <TeamNameAndType displayName={team.displayName} products={team.products} />
    <TeamMemberCounts
      activeMembershipCount={team.activeMembershipCount}
      idActiveAdmins={team.idActiveAdmins}
      idEnterprise={idEnterprise}
      idOrganization={team.id}
    />
    <AddToEnterpriseButton
      idOrganization={team.id}
      idEnterprise={idEnterprise}
    />
  </div>
);

export const NonEnterpriseTeamsPage = ({ idEnterprise }: EnterpriseProps) => {
  const { data, loading } = useClaimableOrganizationsQuery({
    variables: {
      id: idEnterprise,
    },
  });

  if (loading) return <Spinner centered />;

  const claimableOrganizations =
    data?.enterprise?.claimableOrganizations.organizations || [];
  const count = data?.enterprise?.claimableOrganizations.count || 0;

  return (
    <div>
      <div className={styles.heading}>
        <PageInfoSection nonEnterpriseTeamCount={count} />
      </div>
      {count === 0 && (
        <p className={styles.emptyStateMessage}>
          {format('no-claimable-organizations')}
        </p>
      )}
      {count > 0 && (
        <>
          <div>
            {claimableOrganizations.map((team) => (
              <Team key={team.id} team={team} idEnterprise={idEnterprise} />
            ))}
          </div>
          {count > MAX_NUM_TEAMS && (
            <p className={styles.limitMessage}>
              {format('limit-teams-message', { maxNumTeams: MAX_NUM_TEAMS })}
            </p>
          )}
        </>
      )}
    </div>
  );
};
